import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Divider, FormControl, FormLabel,
    Input, Modal, ModalDialog, Option, Select, Sheet, Stack, Textarea, Typography,
} from '@mui/joy';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

import { useAdmin } from '../../../AdminContext';
import {
    APIAdminAdsAdvertisers, APIAdminAdsAdvertiserDetail, APIAdminAdsAdvertiserCreate,
    APIAdminAdsAdvertiserUpdate, APIAdminAdsAdvertiserBlock, APIAdminAdsAdvertiserUnblock,
    adminAdsError,
} from '../../../api_admin_ads';
import { formatMoney } from '../../../components/Ads/adsConstants';

const ADV_STATUS = {
    pending: { label: 'Ожидает подтверждения', color: 'warning' },
    active: { label: 'Активен', color: 'success' },
    suspended: { label: 'Приостановлен', color: 'warning' },
    blocked: { label: 'Заблокирован', color: 'danger' },
};

const STATUS_OPTIONS = [
    { value: '', label: 'Все статусы' },
    { value: 'active', label: 'Активные' },
    { value: 'pending', label: 'Ожидают' },
    { value: 'suspended', label: 'Приостановлены' },
    { value: 'blocked', label: 'Заблокированы' },
];

// --- Модалка создания ---
function CreateModal({ open, onClose, onDone }) {
    const { adminToken: token } = useAdmin();
    const [form, setForm] = useState({
        company_name: '', email: '', contact_name: '', phone: '', legal_name: '', legal_inn: '',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setForm({ company_name: '', email: '', contact_name: '', phone: '', legal_name: '', legal_inn: '' });
            setError(null);
        }
    }, [open]);

    const submit = async () => {
        if (!form.company_name.trim() || !form.email.trim()) {
            setError('Название компании и email обязательны'); return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = {};
            Object.entries(form).forEach(([k, v]) => { if (v.trim()) payload[k] = v.trim(); });
            const res = await APIAdminAdsAdvertiserCreate(token, payload);
            onDone(`Рекламодатель «${res.advertiser.company_name}» создан`);
            onClose();
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось создать рекламодателя'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ maxWidth: 520, width: '100%' }}>
                <Typography level="h4">Новый рекламодатель</Typography>
                {error && <Alert color="danger" sx={{ my: 1 }}>{error}</Alert>}
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                    <FormControl>
                        <FormLabel>Название компании *</FormLabel>
                        <Input value={form.company_name}
                               onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
                    </FormControl>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Email *</FormLabel>
                            <Input type="email" value={form.email}
                                   onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Контактное лицо</FormLabel>
                            <Input value={form.contact_name}
                                   onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                        </FormControl>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Телефон</FormLabel>
                            <Input value={form.phone}
                                   onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>ИНН</FormLabel>
                            <Input value={form.legal_inn}
                                   onChange={(e) => setForm({ ...form, legal_inn: e.target.value })} />
                        </FormControl>
                    </Stack>
                    <FormControl>
                        <FormLabel>Юр. наименование</FormLabel>
                        <Input value={form.legal_name}
                               onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
                    </FormControl>
                    <Typography level="body-xs" color="neutral">
                        Аккаунт заводится автоматически, email считается подтверждённым.
                        Пароль рекламодатель задаст сам через восстановление.
                    </Typography>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                        <Button loading={saving} onClick={submit}>Создать</Button>
                        <Button variant="outlined" color="neutral" onClick={onClose}>Отмена</Button>
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}

// --- Модалка карточки/редактирования ---
function DetailModal({ advertiserId, onClose, onDone }) {
    const { adminToken: token, has } = useAdmin();
    const canEdit = has('ads.manage');

    const [adv, setAdv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(null);
    const [blockMode, setBlockMode] = useState(false);
    const [blockReason, setBlockReason] = useState('');

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await APIAdminAdsAdvertiserDetail(token, advertiserId);
            setAdv(res.advertiser);
            setForm({
                company_name: res.advertiser.company_name || '',
                contact_name: res.advertiser.contact_name || '',
                phone: res.advertiser.phone || '',
                website: res.advertiser.website || '',
                legal_name: res.advertiser.legal_name || '',
                legal_inn: res.advertiser.legal_inn || '',
            });
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось загрузить'));
        } finally {
            setLoading(false);
        }
    }, [token, advertiserId]);

    useEffect(() => { load(); }, [load]);

    const saveEdit = async () => {
        setSaving(true);
        setError(null);
        try {
            await APIAdminAdsAdvertiserUpdate(token, advertiserId, form);
            onDone('Данные рекламодателя сохранены');
            await load();
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось сохранить'));
        } finally {
            setSaving(false);
        }
    };

    const doBlock = async () => {
        if (!blockReason.trim()) { setError('Укажите причину блокировки'); return; }
        setSaving(true);
        setError(null);
        try {
            const res = await APIAdminAdsAdvertiserBlock(token, advertiserId, blockReason.trim());
            onDone(`Заблокирован, остановлено кампаний: ${res.campaigns_stopped ?? 0}`);
            setBlockMode(false); setBlockReason('');
            await load();
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось заблокировать'));
        } finally {
            setSaving(false);
        }
    };

    const doUnblock = async () => {
        setSaving(true);
        setError(null);
        try {
            const res = await APIAdminAdsAdvertiserUnblock(token, advertiserId);
            onDone(`Разблокирован, возобновлено кампаний: ${res.campaigns_resumed ?? 0}`);
            await load();
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось разблокировать'));
        } finally {
            setSaving(false);
        }
    };

    const st = adv ? (ADV_STATUS[adv.status] || { label: adv.status, color: 'neutral' }) : null;

    return (
        <Modal open onClose={onClose}>
            <ModalDialog sx={{ maxWidth: 620, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
                ) : !adv ? (
                    <Alert color="danger">{error || 'Не найдено'}</Alert>
                ) : (
                    <>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography level="h4">{adv.company_name}</Typography>
                            <Chip color={st.color} variant="soft">{st.label}</Chip>
                        </Stack>
                        {error && <Alert color="danger" sx={{ my: 1 }}>{error}</Alert>}

                        <Stack direction="row" spacing={2} sx={{ my: 1.5 }} flexWrap="wrap">
                            <Box><Typography level="body-xs" color="neutral">Баланс</Typography>
                                <Typography level="body-sm">{formatMoney(adv.balance)} ₽</Typography></Box>
                            <Box><Typography level="body-xs" color="neutral">Потрачено</Typography>
                                <Typography level="body-sm">{formatMoney(adv.total_spent)} ₽</Typography></Box>
                            <Box><Typography level="body-xs" color="neutral">Кампаний</Typography>
                                <Typography level="body-sm">{adv.campaigns_active}/{adv.campaigns_total}</Typography></Box>
                            <Box><Typography level="body-xs" color="neutral">Создан</Typography>
                                <Typography level="body-sm">
                                    {adv.date_create ? format(new Date(adv.date_create), 'd MMM yy', { locale: ru }) : '—'}
                                </Typography></Box>
                        </Stack>

                        {adv.status === 'blocked' && adv.block_reason && (
                            <Alert color="danger" sx={{ mb: 1.5 }}>Причина блокировки: {adv.block_reason}</Alert>
                        )}

                        <Divider sx={{ my: 1 }} />
                        <Typography level="title-sm" sx={{ mb: 1 }}>Данные</Typography>
                        <Stack spacing={1.5}>
                            <FormControl>
                                <FormLabel>Название компании</FormLabel>
                                <Input value={form.company_name} disabled={!canEdit}
                                       onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
                            </FormControl>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Контактное лицо</FormLabel>
                                    <Input value={form.contact_name} disabled={!canEdit}
                                           onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                                </FormControl>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Телефон</FormLabel>
                                    <Input value={form.phone} disabled={!canEdit}
                                           onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                                </FormControl>
                            </Stack>
                            <FormControl>
                                <FormLabel>Сайт</FormLabel>
                                <Input value={form.website} disabled={!canEdit}
                                       onChange={(e) => setForm({ ...form, website: e.target.value })} />
                            </FormControl>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Юр. наименование</FormLabel>
                                    <Input value={form.legal_name} disabled={!canEdit}
                                           onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
                                </FormControl>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>ИНН</FormLabel>
                                    <Input value={form.legal_inn} disabled={!canEdit}
                                           onChange={(e) => setForm({ ...form, legal_inn: e.target.value })} />
                                </FormControl>
                            </Stack>
                        </Stack>

                        {canEdit && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                {blockMode ? (
                                    <Stack spacing={1.5}>
                                        <FormControl>
                                            <FormLabel>Причина блокировки (покажется при входе)</FormLabel>
                                            <Textarea minRows={2} value={blockReason}
                                                      onChange={(e) => setBlockReason(e.target.value)} />
                                        </FormControl>
                                        <Stack direction="row" spacing={1.5}>
                                            <Button color="danger" loading={saving} onClick={doBlock}>Подтвердить блокировку</Button>
                                            <Button variant="outlined" color="neutral" onClick={() => setBlockMode(false)}>Отмена</Button>
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1.5} flexWrap="wrap">
                                        <Button loading={saving} onClick={saveEdit}>Сохранить данные</Button>
                                        {adv.status === 'blocked' ? (
                                            <Button variant="outlined" color="success" loading={saving} onClick={doUnblock}>
                                                Разблокировать
                                            </Button>
                                        ) : (
                                            <Button variant="outlined" color="danger" onClick={() => setBlockMode(true)}>
                                                Заблокировать
                                            </Button>
                                        )}
                                        <Button variant="plain" color="neutral" onClick={onClose}>Закрыть</Button>
                                    </Stack>
                                )}
                            </>
                        )}
                    </>
                )}
            </ModalDialog>
        </Modal>
    );
}

export default function AdminAdsAdvertisersPage() {
    const { adminToken: token, has } = useAdmin();

    const [items, setItems] = useState([]);
    const [counters, setCounters] = useState({});
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [detailId, setDetailId] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => { setPage(1); setSearch(searchInput); }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdminAdsAdvertisers(token, {
                status: status || undefined, search: search || undefined,
                page, page_size: 30,
            });
            setItems(res?.items || []);
            setCounters(res?.counters || {});
            setPages(res?.pages || 1);
            setTotal(res?.total || 0);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось загрузить рекламодателей'));
        } finally {
            setLoading(false);
        }
    }, [token, status, search, page]);

    useEffect(() => { load(); }, [load]);

    const onDone = (msg) => { setToast(msg); load(); };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography level="h4" sx={{ mb: 0.5 }}>Рекламодатели</Typography>
                    <Typography level="body-sm" color="neutral">Найдено: {total}</Typography>
                </Box>
                {has('ads.manage') && (
                    <Button startDecorator={<AddRoundedIcon />} onClick={() => setCreateOpen(true)}>
                        Новый рекламодатель
                    </Button>
                )}
            </Stack>

            {toast && <Alert color="success" sx={{ mb: 2 }} endDecorator={
                <Button size="sm" variant="plain" color="neutral" onClick={() => setToast(null)}>×</Button>
            }>{toast}</Alert>}
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                    <Input size="sm" placeholder="Название, email, ИНН" startDecorator={<SearchRoundedIcon />}
                           value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                           sx={{ flex: 1, minWidth: 240 }} />
                    <Select size="sm" value={status} sx={{ minWidth: 190 }}
                            onChange={(_, v) => { setStatus(v ?? ''); setPage(1); }}>
                        {STATUS_OPTIONS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                    </Select>
                    <Stack direction="row" spacing={1}>
                        <Chip size="sm" color="success" variant="soft">активны: {counters.active ?? 0}</Chip>
                        <Chip size="sm" color="warning" variant="soft">ожидают: {counters.pending ?? 0}</Chip>
                        <Chip size="sm" color="danger" variant="soft">блок: {counters.blocked ?? 0}</Chip>
                    </Stack>
                </Stack>
            </Sheet>

            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                    <Table hoverRow stickyHeader sx={{ '--TableCell-paddingY': '10px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 60 }}>ID</th>
                                <th>Компания</th>
                                <th style={{ width: 220 }}>Email</th>
                                <th style={{ width: 150 }}>Статус</th>
                                <th style={{ width: 120 }}>Баланс</th>
                                <th style={{ width: 110 }}>Кампаний</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((a) => {
                                const st = ADV_STATUS[a.status] || { label: a.status, color: 'neutral' };
                                return (
                                    <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => setDetailId(a.id)}>
                                        <td>{a.id}</td>
                                        <td><Typography level="body-sm" sx={{ fontWeight: 600 }}>{a.company_name}</Typography></td>
                                        <td><Typography level="body-sm">{a.email}</Typography></td>
                                        <td><Chip size="sm" color={st.color} variant="soft">{st.label}</Chip></td>
                                        <td>{formatMoney(a.balance)} ₽</td>
                                        <td>{a.campaigns_active}/{a.campaigns_total}</td>
                                    </tr>
                                );
                            })}
                            {items.length === 0 && (
                                <tr><td colSpan={6}>
                                    <Typography level="body-sm" color="neutral" sx={{ py: 3, textAlign: 'center' }}>
                                        Ничего не найдено
                                    </Typography>
                                </td></tr>
                            )}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            {pages > 1 && (
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                    <Button size="sm" variant="outlined" color="neutral"
                            disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Назад</Button>
                    <Typography level="body-sm" sx={{ alignSelf: 'center' }}>{page} / {pages}</Typography>
                    <Button size="sm" variant="outlined" color="neutral"
                            disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Вперёд</Button>
                </Stack>
            )}

            <CreateModal open={createOpen} onClose={() => setCreateOpen(false)} onDone={onDone} />
            {detailId && (
                <DetailModal advertiserId={detailId} onClose={() => setDetailId(null)} onDone={onDone} />
            )}
        </Box>
    );
}